<template>
  <section class="settings-card">
    <div class="card-header">
      <div>
        <h2 class="card-title blue">客户管理</h2>
        <p class="card-desc">管理项目关联的客户账号，支持导入和手动分配</p>
      </div>
      <div style="display:flex;gap:10px;align-items:center">
        <el-input v-model="searchKeyword" placeholder="搜索用户名/姓名" prefix-icon="el-icon-search" clearable size="small" style="width:200px"></el-input>
        <el-button type="warning" size="small" @click="exportTemplate">导出客户模板</el-button>
        <el-upload :action="uploadUrl" :headers="uploadHeaders" :data="{ projectId: projectId }" :on-success="onImportSuccess" :on-error="onImportError" :show-file-list="false" accept=".xlsx,.xls" style="display:inline-block">
          <el-button type="success" size="small">导入客户</el-button>
        </el-upload>
        <el-button type="primary" size="small" class="add-btn" @click="showAssignDialog">
          <i class="el-icon-s-custom"></i> 分配已有客户
        </el-button>
      </div>
    </div>
    <div class="member-table-wrap">
      <table class="member-table">
        <thead>
          <tr><th>用户名</th><th>姓名</th><th>手机号</th><th>客户类型</th><th>加入时间</th><th>操作</th></tr>
        </thead>
        <tbody>
          <tr v-for="c in pagedList" :key="c.id">
            <td>{{ c.username }}</td>
            <td>{{ c.name || '-' }}</td>
            <td class="td-muted">{{ c.phone || '-' }}</td>
            <td><span class="role-badge" style="background:#fdf6ec;color:#e6a23c">{{ clientTypeMap[c.dataType] || '客户' }}</span></td>
            <td class="td-muted">{{ formatDate(c.createdTime) }}</td>
            <td>
              <el-button type="text" size="mini" @click="showEditClient(c)" style="color:#e6a23c">编辑</el-button>
              <el-button type="text" size="mini" @click="$emit('show-perm', c, 'client')" style="color:#1476ff">分配权限</el-button>
              <i class="el-icon-s-custom action-icon danger" title="移除" @click="removeClient(c)" style="margin-left:8px"></i>
            </td>
          </tr>
          <tr v-if="clients.length === 0">
            <td colspan="6" style="text-align:center;color:#94a3b8;padding:32px 0;">暂无客户</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="pagination" v-if="filteredList.length > pageSize">
      <el-pagination
        background
        layout="total, prev, pager, next"
        :total="filteredList.length"
        :page-size="pageSize"
        :current-page.sync="currentPage"
        @current-change="p => currentPage = p" />
    </div>

    <!-- 分配客户弹窗 -->
    <el-dialog :visible.sync="assignDialogVisible" width="480px" append-to-body custom-class="add-member-dialog" :show-close="false">
      <div class="dlg-header">
        <h3 class="dlg-title">分配客户</h3>
        <i class="el-icon-close dlg-close" @click="assignDialogVisible = false"></i>
      </div>
      <div class="dlg-body">
        <div class="dlg-section">
          <label class="dlg-label">搜索用户</label>
          <el-input v-model="assignKeyword" placeholder="输入用户名/姓名/手机号" @input="debounceSearch" clearable class="dlg-select" />
        </div>
        <div class="dlg-section" v-if="assignResults.length > 0">
          <label class="dlg-label">搜索结果</label>
          <el-radio-group v-model="assignSelectedUid" style="width:100%">
            <div v-for="u in assignResults" :key="u.uid" style="margin-bottom:8px">
              <el-radio :label="u.uid">{{ u.name }} ({{ u.username }}) {{ u.phone }}</el-radio>
            </div>
          </el-radio-group>
        </div>
      </div>
      <div class="dlg-footer">
        <el-button @click="assignDialogVisible = false" class="dlg-cancel-btn">取消</el-button>
        <el-button type="primary" @click="doAssign" :disabled="!assignSelectedUid" class="dlg-confirm-btn">确定</el-button>
      </div>
    </el-dialog>

    <!-- 编辑客户弹窗 -->
    <el-dialog title="编辑客户" :visible.sync="editDialogVisible" width="440px" custom-class="perm-dialog" :close-on-click-modal="false">
      <el-form :model="editForm" label-width="80px" v-if="editUser">
        <el-form-item label="用户名"><el-input :value="editUser.username" disabled></el-input></el-form-item>
        <el-form-item label="姓名"><el-input v-model="editForm.name" placeholder="修改姓名（留空不改）"></el-input></el-form-item>
        <el-form-item label="手机"><el-input v-model="editForm.phone" placeholder="修改手机（留空不改）"></el-input></el-form-item>
        <el-form-item label="新密码"><el-input v-model="editForm.password" placeholder="输入新密码（留空不改）" show-password></el-input></el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="editDialogVisible = false" class="dlg-cancel-btn">取消</el-button>
        <el-button type="primary" @click="doUpdateClient" :loading="editSaving" class="dlg-confirm-btn">保存</el-button>
      </div>
    </el-dialog>
  </section>
</template>

<script>
import { deleteProjectMember } from '@/app/index/api/project/manager'
import { formatDate, downloadBlob } from './settingsHelpers'

export default {
  name: 'ClientSection',
  props: {
    projectId: { type: [String, Number], required: true },
    clients: { type: Array, default: () => [] }
  },
  data() {
    return {
      searchKeyword: '',
      clientTypeMap: { 1: '全国', 2: '大区', 3: '小区', 4: '经销商' },
      assignDialogVisible: false,
      assignKeyword: '',
      assignResults: [],
      assignSelectedUid: null,
      editDialogVisible: false,
      editUser: null,
      editForm: { name: '', phone: '', password: '' },
      editSaving: false,
      currentPage: 1,
      pageSize: 10
    }
  },
  computed: {
    filteredList() {
      if (!this.searchKeyword) return this.clients
      const kw = this.searchKeyword.toLowerCase()
      return this.clients.filter(c =>
        (c.username || '').toLowerCase().includes(kw) ||
        (c.name || '').toLowerCase().includes(kw)
      )
    },
    pagedList() {
      const start = (this.currentPage - 1) * this.pageSize
      return this.filteredList.slice(start, start + this.pageSize)
    },
    uploadUrl() {
      return (process.env.VUE_APP_BASE_URL || '') + '/ProjectManager/project/importClients'
    },
    uploadHeaders() {
      return { Authorization: 'Bearer ' + (localStorage.getItem('ACCESS_TOKEN') || '') }
    }
  },
  watch: {
    searchKeyword() { this.currentPage = 1 },
    clients() { this.currentPage = 1 }
  },
  methods: {
    formatDate,
    // 适配项目封装的 axios.get(url, params, options) 签名
    async exportTemplate() {
      try {
        const res = await this.axios.get(
          '/ProjectManager/project/exportClientTemplate',
          { projectId: this.projectId },
          { responseType: 'blob' }
        )
        downloadBlob(res, '客户导入模板.xlsx')
      } catch (e) { this.$message.error('导出模板失败') }
    },
    onImportSuccess(res) {
      const data = res.data || res
      if (data.message || data.created !== undefined) {
        this.$message.success(data.message || `导入完成：新建${data.created || 0}，关联${data.linked || 0}，跳过${data.skipped || 0}`)
        this.$emit('refresh')
      } else {
        this.$message.error(data.message || '导入失败')
      }
    },
    onImportError() { this.$message.error('导入失败') },
    showAssignDialog() {
      this.assignKeyword = ''
      this.assignResults = []
      this.assignSelectedUid = null
      this.assignDialogVisible = true
    },
    debounceSearch() {
      clearTimeout(this._timer)
      this._timer = setTimeout(() => this.doSearch(), 300)
    },
    async doSearch() {
      if (!this.assignKeyword) { this.assignResults = []; return }
      // 文档 53 P1-03：搜索接口已加 projectId + 角色边界
      const { data } = await this.axios.get('/ProjectManager/project/searchUsers', {
        params: { projectId: this.projectId, keyword: this.assignKeyword, role: 'client' }
      })
      this.assignResults = data.data || []
    },
    async doAssign() {
      if (!this.assignSelectedUid) return
      try {
        await this.axios.post('/ProjectManager/project/assignUser', {
          ProjectId: parseInt(this.projectId),
          UserId: this.assignSelectedUid,
          Role: 'client'
        })
        this.$message.success('分配成功')
        this.assignDialogVisible = false
        this.$emit('refresh')
      } catch (e) { this.$message.error(e.response?.data?.message || '分配失败') }
    },
    showEditClient(c) {
      this.editUser = c
      this.editForm = { name: c.name || '', phone: c.phone || '', password: '' }
      this.editDialogVisible = true
    },
    // 🔧 Bug修复: 使用 axios 替代 fetch
    async doUpdateClient() {
      const f = this.editForm
      if (!f.name && !f.phone && !f.password) {
        return this.$message.warning('请至少填写一项需要修改的内容')
      }
      this.editSaving = true
      try {
        const res = await this.axios.post('/ProjectManager/project/updateClientPassword', {
          projectId: Number(this.projectId),
          uid: this.editUser.uid || this.editUser.userId,
          password: f.password || '',
          name: f.name || '',
          phone: f.phone || ''
        })
        const data = res?.data
        if (data?.errcode === 0) {
          this.$message.success('修改成功')
          this.editDialogVisible = false
          this.$emit('refresh')
        } else {
          this.$message.error(data?.message || '修改失败')
        }
      } catch (e) { this.$message.error('修改失败') }
      finally { this.editSaving = false }
    },
    removeClient(row) {
      const name = row.name || row.username || '该成员'
      this.$confirm(`确定要移除 ${name} 吗？`, '提示', { type: 'warning', customClass: 'settings-confirm-dialog' })
        .then(async () => {
          try {
            await deleteProjectMember(row.id)
            this.$message.success('已移除')
            this.$emit('refresh')
          } catch (e) {
            if (e !== 'cancel' && e !== 'close') this.$message.error(e?.data?.message || e?.message || '移除失败')
          }
        }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.pagination { margin-top: 16px; text-align: right; }
</style>
